/**
 * Given a condition and a comparator object of completed/tag/hidden IDs, returns whether the condition is fulfilled.
 *
 * A condition may be:
 *
 * - an ID to fulfill
 * - a string of IDs separated by whitespace, all of which must be fulfilled
 * - an array of conditions, all of which must be fulfilled
 * - an object with `any` and `of` properties, where `of` is a string or array of conditions, and `any` is the minimum number of those conditions that must be fulfilled
 *
 * Any ID may be negated with a leading `!`, in which case it must not be fulfilled.
 *
 * @param condition
 * @param comparator
 * @returns
 */
export default function evaluateCondition(
  condition: Condition,
  comparator: Record<string, boolean>
): Boolean {
  if (!condition) return true;

  // array
  if (Array.isArray(condition)) {
    return condition.every(con => evaluateCondition(con, comparator));
  }

  const evalId = (id: string) =>
    id[0] === '!' ? !comparator[id.slice(1)] : comparator[id];

  // string
  if (typeof condition === 'string')
    return condition.split(/\s+/).every(evalId);

  // any...of...
  if (condition.any) {
    const { any, of } = condition;
    let count = 0;

    if (typeof of === 'string') {
      for (const id of of.split(/\s+/)) {
        if (evalId(id)) count++;
        if (count >= any) return true;
      }
    }

    if (Array.isArray(of)) {
      for (const con of of) {
        if (evaluateCondition(con, comparator)) count++;
        if (count >= any) return true;
      }
    }
  }

  return false;
}

export function computeConditionIds(condition: Condition) {
  const ids = new Set<string>();

  // recurse through condition and extract IDs
  const recurse = (con: Condition) => {
    if (!con) return;

    if (typeof con === 'string') {
      con.split(/\s+/).forEach(id => ids.add(id));
    } else if (Array.isArray(con)) {
      con.forEach(recurse);
    } else if (con.any) {
      recurse(con.of);
    }
  };

  recurse(condition);

  return [...ids];
}
